import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box } from "frontend/ui-components/design-system/box/box";
import { useAtom, useAtomValue } from "jotai";
import { selectedElementIdsAtom, SidepanelType, sidePanelType, syncServiceAtom } from "state-atoms";
import styles from "./frames-view.module.css";
import cn from "classnames";
import { frameIcon as FrameIcon } from "frontend/canvas-designer-new/sidebar/side-menu-items/side-menu-icons";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import { useFooterContext } from "frontend/context/footer-context";
import { useSortedFrames } from "subscriptions";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { FrameThumbnail } from "frontend/canvas-designer-new/frames/frame-thumbnail/frame-thumbnail";
import { useCallback } from "react";
import { ClosedEyeIcon } from "frontend/icons/closed-eye-icon";
import { FrameActions } from "frontend/canvas-designer-new/frames/frame-actions/frame-actions";
import { ElementDrawProps } from "shared/datamodel/canvas-element";
import { useHeaderContext } from "frontend/context/header-context";
import { CursorType } from "frontend/canvas-designer-new/cursor-type";

export const FramesView = () => {
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
  const syncService = useAtomValue(syncServiceAtom);
  const { setCursorType } = useHeaderContext();
  const { onFocusElement, onFrameDeleted } = useFooterContext();
  const { sortedFrames } = useSortedFrames(syncService?.getReplicache());
  const isCIM = useIsCIMReorderEnabled();
  const [selectedElementIds, setSelectedElementIds] = useAtom(selectedElementIdsAtom);
  const isOverflowing = sortedFrames.length > 5;

  const onFrameThumbnailClick = useCallback(
    (frame: any) => () => {
      onFocusElement(`frame-${frame.id}`);
      setSelectedElementIds([`frame-${frame.id}`]);
    },
    [onFocusElement, setSelectedElementIds]
  );

  const changeFrameVisibility = useCallback(
    (id: string, visible: boolean) => {
      syncService?.mutate.changeElementProperties({
        id,
        properties: [{ key: "visible", value: visible }],
      });
    },
    [syncService]
  );

  const changeFrameTitle = useCallback(
    (id: string, title: string) => {
      syncService?.mutate.changeElementProperties({
        id,
        properties: [{ key: "title", value: title }],
      });
    },
    [syncService]
  );

  const onDragEnd = useCallback(
    (result: any) => {
      const { source, destination } = result;
      if (!source || !destination) {
        return;
      }
      const fromIndex = source.index;
      const toIndex = destination.index;
      const frame = sortedFrames[fromIndex];
      sortedFrames.splice(fromIndex, 1);
      sortedFrames.splice(toIndex, 0, frame);

      const elements: [string, ElementDrawProps][] = sortedFrames.map((frame, index) => [
        `${frame.type}-${frame.id}`,
        { sortIndex: index },
      ]);
      syncService?.mutate.changeElements({ info: elements });
    },
    [sortedFrames, syncService]
  );

  if (isCIM && openedSidePanel !== SidepanelType.frames) {
    return null;
  }
  if (isCIM && sortedFrames.length === 0) {
    return null;
  }

  return (
    <Box variant="modal" className={cn(styles.layout)} gap="0px">
      <div className={styles.header}>
        {FrameIcon}
        <div className={styles.title}>Frames</div>
      </div>
      <div
        className={styles.closeIcon}
        onClick={() => {
          setOpenedSidePanel(SidepanelType.none);
          setCursorType(CursorType.pointer);
        }}
      >
        <CloseIcon color="#848199" />
      </div>
      <div className={styles.separator}></div>

      <div
        className={styles.framesContainer}
        style={{ width: isOverflowing ? "103%" : "106%", justifyItems: isOverflowing ? "start" : "center" }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {sortedFrames.map((frame, index) => {
                  const { visible = true } = frame.value;
                  return (
                    <Draggable key={frame.id} draggableId={frame.id} index={index}>
                      {(provided) => (
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className={styles.frameCell}
                        >
                          <FrameActions
                            key={frame.id}
                            id={frame.id}
                            frame={frame.value}
                            isSelected={selectedElementIds.includes(`frame-${frame.id}`)}
                            onFrameThumbnailClick={onFrameThumbnailClick(frame)}
                            onFrameVisibilityChange={changeFrameVisibility}
                            onFrameTitleChange={changeFrameTitle}
                            onFrameDelete={onFrameDeleted}
                          >
                            {visible ? (
                              <FrameThumbnail
                                key={frame.id}
                                syncService={syncService}
                                frame={frame}
                                width="152px"
                                height="87px"
                                className={styles.frameThumbnail}
                              />
                            ) : (
                              <div className={styles.hiddenFrameThumbnail}>
                                <ClosedEyeIcon color={"#7F869B"} size={17} />
                                <div>Hidden frame</div>
                              </div>
                            )}
                          </FrameActions>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </Box>
  );
};
